/**
 * views/Kitchen/OpsKitchenBoard.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The platform triage board. Every live kitchen on Lusuku, at once, for Lusuku
 * staff.
 *
 * ## The order on this screen is the answer
 *
 * `getOpsBoard` already sorts the kitchens worst first: breaches, then late,
 * then oldest open ticket. That is the order an operations lead would walk the
 * floor in, and it is the whole product. Re-sorting alphabetically, or by name,
 * or "so it stops jumping around" would destroy the one thing the board is for.
 * The tickets inside a kitchen arrive oldest first for the same reason. Neither
 * order is touched here.
 *
 * ## Why this looks like a ward chart
 *
 * The first version of this screen was a dark card floating inside the light
 * dashboard, holding a second card, holding five tiles that each had a coloured
 * top border and a coloured numeral. Four levels of container and five competing
 * hues to say one thing. It read as unfinished because it was over-drawn.
 *
 * What replaced it follows three rules:
 *
 *   1. ONE surface. Rules divide it; nothing is enclosed in a second box. A
 *      hairline separates without adding a shape to look at.
 *   2. Numbers wear ink. A small coloured pip beside the label carries the
 *      state, and it disappears at zero, so an idle board is genuinely quiet
 *      rather than a wall of lit indicators saying nothing.
 *   3. The list is a table, because it is a table. Right-aligned tabular
 *      figures in fixed columns can be scanned down in one pass; the same
 *      numbers scattered through prose cannot.
 *
 * ## Two numbers that do not describe the same set
 *
 * The `stats` block is a live rollup for the whole country, computed
 * independently of the `breachedOnly` filter. The `kitchens` array does respect
 * it. So with the filter on, the header can legitimately read 40 open tickets
 * while the rows below add up to 6. The copy says so, in one short line, instead
 * of letting an operator conclude the board is broken.
 *
 * ## Ages keep moving between polls
 *
 * The server bakes `ageMs` at the instant it answered. A board that only
 * refreshes every 15 seconds would show frozen ages for 15 seconds at a time,
 * which reads as a dead screen. Displayed ages add the wall clock elapsed since
 * OUR fetch landed, so the numbers keep climbing without ever compounding any
 * clock skew between this browser and the server.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { apiKitchenList, apiKitchenOpsBoard } from "../../api/index";
import {
  KDS,
  STATUS_META,
  SLA_META,
  ageColour,
  pipFor,
  formatDuration,
  formatClock,
  formatPercent,
  NUM_TABULAR,
  NUM_DISPLAY,
  MICRO_LABEL,
} from "./kitchenUi";
import AppSelect from "../../components/ui/AppSelect";

/** Poll cadence. Fast enough to catch a kitchen falling over, slow enough not to hammer the API. */
const REFRESH_MS = 15000;
/** Separate, cheaper tick that only moves the ages and the "updated Xs ago" label. */
const TICK_MS = 1000;

/** Worst state present in a kitchen. Drives the row's severity bar, worst wins. */
function worstStateOf(kitchen) {
  if (Number(kitchen?.breached) > 0) return "BREACHED";
  if (Number(kitchen?.late) > 0) return "LATE";
  if (Number(kitchen?.atRisk) > 0) return "AT_RISK";
  return "ON_TIME";
}

const countOf = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const S = {
  /**
   * Negative margin so the dark surface reaches the edges of the admin content
   * area. Without it the panel floats on the light dashboard ground with a grey
   * gutter all the way round, which is what made it look like a widget that had
   * been dropped in rather than a screen.
   */
  page: {
    background: KDS.bgPanel,
    minHeight: "calc(100vh - 120px)",
    margin: "-15px",
    color: KDS.text,
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    padding: "16px 22px",
    borderBottom: `1px solid ${KDS.border}`,
  },
  title: { margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", color: KDS.text },
  sub: { margin: "3px 0 0", fontSize: 11.5, color: KDS.textDim, lineHeight: 1.5 },
  controls: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  control: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: KDS.radiusSm,
    border: `1px solid ${KDS.borderBright}`,
    background: "transparent",
    color: KDS.textMid,
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  controlOn: {
    background: KDS.bgTile,
    color: KDS.text,
    borderColor: "#3c527c",
  },
  live: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: KDS.textDim },
  liveDot: (colour) => ({
    width: 7,
    height: 7,
    borderRadius: 999,
    background: colour,
    boxShadow: `0 0 0 3px ${colour}22`,
    flex: "none",
  }),

  // ── The KPI row: cells divided by hairlines, never boxes ──────────────────
  kpi: { display: "flex", alignItems: "stretch", borderBottom: `1px solid ${KDS.border}` },
  cell: (first, hero) => ({
    flex: hero ? 1.25 : 1,
    minWidth: 0,
    padding: "16px 22px",
    borderLeft: first ? "none" : `1px solid ${KDS.border}`,
    background: hero ? KDS.bgTile : "transparent",
  }),
  cellHead: { display: "flex", alignItems: "center", gap: 7 },
  pip: (colour) => ({ width: 6, height: 6, borderRadius: 999, background: colour, flex: "none" }),
  cellValue: (hero, muted) => ({
    ...NUM_DISPLAY,
    fontSize: hero ? 42 : 30,
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: 7,
    color: muted ? KDS.textDim : KDS.text,
  }),
  note: {
    padding: "11px 22px",
    fontSize: 11.5,
    lineHeight: 1.6,
    color: KDS.textDim,
    borderBottom: `1px solid ${KDS.border}`,
  },

  // ── The list: a real table ────────────────────────────────────────────────
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    ...MICRO_LABEL,
    textAlign: "left",
    padding: "10px 22px",
    borderBottom: `1px solid ${KDS.border}`,
    background: "rgba(255,255,255,0.012)",
    whiteSpace: "nowrap",
  },
  thR: { textAlign: "right" },
  td: {
    padding: "12px 22px",
    borderBottom: `1px solid ${KDS.border}`,
    fontSize: 13.5,
    color: KDS.textMid,
    verticalAlign: "middle",
  },
  tdR: { textAlign: "right", ...NUM_TABULAR, color: KDS.text, fontWeight: 600 },
  /**
   * A zero in a counter column is not news. Recessing it means a scan down the
   * column lands on the figures that are actually non-zero, which in a table of
   * mostly-empty severity counters is nearly all of the reading work.
   */
  tdNum: (value) => ({
    textAlign: "right",
    ...NUM_TABULAR,
    fontWeight: 600,
    color: Number(value) > 0 ? KDS.text : KDS.textDim,
  }),
  /** The colour legend lives in the column header, not repeated on every row. */
  thPip: (colour) => ({
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: 999,
    background: colour,
    marginRight: 6,
    verticalAlign: "middle",
  }),
  scroller: { overflowX: "auto" },
  sevCell: { width: 34, paddingLeft: 22, paddingRight: 0 },
  sev: (colour) => ({
    display: "block",
    width: 3,
    height: 30,
    borderRadius: 2,
    background: colour,
  }),
  name: { color: KDS.text, fontWeight: 600, fontSize: 14 },
  nameSub: { display: "block", fontWeight: 400, color: KDS.textDim, fontSize: 11.5, marginTop: 2 },
  /**
   * `solid` is not decoration. Late and breached are the same hue family by
   * design (severity is a sequence, not two identities), which at chip size
   * makes them near-identical: the validator puts them 8.9 apart, well under
   * the readable floor. Filling the worst one adds a second, non-colour channel
   * so the difference survives a glance, a cheap panel, and colourblindness.
   */
  chip: (colour, solid) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
    color: solid ? KDS.actionInk : colour,
    border: `1px solid ${solid ? colour : `${colour}59`}`,
    background: solid ? colour : `${colour}17`,
  }),
  /**
   * Quiet on purpose. This is an affordance, not data, and it sat in the same
   * uppercase bold as the figures it was next to, competing with them for a
   * reader who was trying to scan a column of numbers.
   */
  rowButton: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    color: KDS.textMid,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
  },

  // ── Expanded tickets, indented under their kitchen ───────────────────────
  subWrap: { padding: "2px 22px 14px 56px", background: KDS.bgLane },
  subRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "9px 12px",
    background: "none",
    border: "none",
    borderBottom: `1px solid ${KDS.border}`,
    font: "inherit",
    color: KDS.textMid,
    textAlign: "left",
    cursor: "pointer",
  },
  code: {
    ...NUM_TABULAR,
    fontSize: 13,
    fontWeight: 700,
    color: KDS.text,
    minWidth: 62,
  },

  empty: { padding: "54px 22px", textAlign: "center" },
  emptySub: { fontSize: 12.5, color: KDS.textDim, marginTop: 8 },
  errorStrip: {
    padding: "10px 22px",
    fontSize: 12.5,
    color: KDS.atRisk,
    background: KDS.dangerSoft,
    borderBottom: `1px solid ${KDS.border}`,
  },
};

export default function OpsKitchenBoard({ onOpenTicket }) {
  const history = useHistory();

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breachedOnly, setBreachedOnly] = useState(false);
  const [country, setCountry] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [fetchedAt, setFetchedAt] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  /**
   * `silent` is an ARGUMENT, not state, which is what lets this callback depend
   * on nothing but the two filters. The interval below can then hold a genuinely
   * correct dependency array instead of a suppression comment.
   */
  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const params = {};
        if (country) params.country = country;
        if (breachedOnly) params.breachedOnly = "1";

        const res = await apiKitchenOpsBoard(params);
        const payload = res?.data?.data || null;
        setBoard(payload);
        setFetchedAt(Date.now());
        setError(null);

        /**
         * Countries seen in live traffic are MERGED into the list rather than
         * replacing it. The country filter narrows what comes back, so a list
         * rebuilt from the current response would delete every other option the
         * moment one was chosen and strand the operator on it.
         *
         * This is only a top-up. The authoritative list is loaded separately
         * from the kitchens endpoint, because deriving it from the board alone
         * meant an idle platform produced NO options at all: with nothing open,
         * nothing was ever added, and the dropdown sat permanently on "All
         * countries" with nothing to pick.
         */
        setCountryOptions((prev) => {
          const next = new Set(prev);
          (payload?.kitchens || []).forEach((kitchen) => {
            if (kitchen?.country) next.add(String(kitchen.country).toUpperCase());
          });
          return next.size === prev.length ? prev : Array.from(next).sort();
        });
      } catch (err) {
        // A failed poll must never blank a wall board. Keep whatever is on
        // screen, say it is stale, and let the next tick recover.
        setError(err?.response?.data?.message || "Could not refresh the operations board.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [country, breachedOnly]
  );

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Every country this account can actually reach, whether or not anything is
   * cooking in it. `/api/kitchen/kitchens` is the right source: it lists the
   * restaurants the caller may open and it is country-scoped server-side, so
   * the dropdown can never offer a market this user would be refused. Runs
   * once, not on every filter change, because the set of markets does not move.
   */
  useEffect(() => {
    let cancelled = false;
    apiKitchenList()
      .then(({ data }) => {
        if (cancelled) return;
        const seen = new Set();
        (data?.data?.kitchens || []).forEach((kitchen) => {
          if (kitchen?.country) seen.add(String(kitchen.country).toUpperCase());
        });
        setCountryOptions((prev) => {
          const next = new Set(prev);
          seen.forEach((code) => next.add(code));
          return next.size === prev.length ? prev : Array.from(next).sort();
        });
      })
      .catch(() => {
        // Non-fatal. The board still works; the operator just loses the filter.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Deliberately separate from the poll: this one only moves labels, so it
  // never depends on the fetcher and never restarts when a filter changes.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const toggleKitchen = useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const openTicket = useCallback(
    (ticket, kitchen) => {
      if (typeof onOpenTicket === "function") {
        onOpenTicket(ticket?._id);
        return;
      }
      history.push(
        `/admin/kitchen-board?vendorId=${encodeURIComponent(String(kitchen?.vendorId || ""))}`
      );
    },
    [onOpenTicket, history]
  );

  /** Wall clock elapsed since our own fetch landed. Never negative. */
  const drift = fetchedAt ? Math.max(0, nowMs - fetchedAt) : 0;
  const secondsAgo = Math.round(drift / 1000);
  const liveAge = (bakedMs) => (Number.isFinite(Number(bakedMs)) ? Number(bakedMs) + drift : null);

  const kitchens = board?.kitchens || [];
  const stats = board?.stats || {};

  // Reads `board` rather than the derived `stats` object above: a fresh `{}`
  // fallback every render would make the dependency array lie.
  const headline = useMemo(() => {
    const live = board?.stats || {};
    return [
      { key: "open", label: "Open tickets", value: countOf(live.openTickets), accent: null, hero: true },
      {
        key: "unacknowledged",
        label: "Unaccepted",
        value: countOf(live.unacknowledged),
        accent: STATUS_META.NEW.colour,
      },
      { key: "atRisk", label: SLA_META.AT_RISK.label, value: countOf(live.atRisk), accent: SLA_META.AT_RISK.colour },
      { key: "late", label: SLA_META.LATE.label, value: countOf(live.late), accent: SLA_META.LATE.colour },
      { key: "breached", label: SLA_META.BREACHED.label, value: countOf(live.breached), accent: SLA_META.BREACHED.colour },
    ];
  }, [board]);

  const openTotal = countOf(stats.openTickets);
  const breachShare = openTotal ? countOf(stats.breached) / openTotal : null;
  const stale = secondsAgo > REFRESH_MS / 1000 + 10;

  return (
    <div style={S.page}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={S.bar}>
        <div style={{ minWidth: 0 }}>
          <h3 style={S.title}>Live kitchens</h3>
          <p style={S.sub}>Worst first. Work down the list.</p>
        </div>

        <div style={S.controls}>
          <AppSelect
            style={{ ...S.control, paddingRight: 8 }}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            aria-label="Country"
          >
            <option value="">All countries</option>
            {countryOptions.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </AppSelect>

          <button
            type="button"
            style={breachedOnly ? { ...S.control, ...S.controlOn } : S.control}
            onClick={() => setBreachedOnly((prev) => !prev)}
            aria-pressed={breachedOnly}
          >
            Breached only
          </button>

          <button type="button" style={S.control} onClick={() => load()} disabled={loading}>
            {loading ? "Refreshing" : "Refresh"}
          </button>

          <span style={S.live}>
            <span style={S.liveDot(stale ? KDS.atRisk : KDS.onTime)} />
            {!fetchedAt
              ? "Not loaded"
              : stale
              ? `Stale, ${secondsAgo}s`
              : `Live, ${REFRESH_MS / 1000}s`}
          </span>
        </div>
      </div>

      {/* ── Headline counts ────────────────────────────────────────────── */}
      <div style={S.kpi}>
        {headline.map((item, index) => {
          // No pip at zero. A colour that is always lit stops meaning anything.
          const pip = item.accent ? pipFor(item.value, item.accent) : null;
          return (
            <div key={item.key} style={S.cell(index === 0, item.hero)}>
              <div style={S.cellHead}>
                {pip ? <span style={S.pip(pip)} /> : null}
                <span style={MICRO_LABEL}>{item.label}</span>
              </div>
              <div style={S.cellValue(item.hero, item.value === 0)}>{item.value}</div>
            </div>
          );
        })}
      </div>

      <div style={S.note}>
        {openTotal === 0
          ? "Nothing open right now."
          : `${formatPercent(breachShare)} of open tickets are in breach.`}{" "}
        These totals cover every open ticket
        {country ? ` in ${country}` : " on the platform"} and ignore the breached only filter, so
        with it on the rows below add up to less.
        {board?.serverTime ? ` Server clock ${formatClock(board.serverTime)}.` : ""}
      </div>

      {error ? (
        <div style={S.errorStrip}>
          {error} Showing the last board that loaded. The next refresh will try again.
        </div>
      ) : null}

      {/* ── The list ───────────────────────────────────────────────────── */}
      {loading && !board ? (
        <div style={S.empty}>
          <span style={{ fontSize: 14, color: KDS.textMid }}>Loading the board</span>
        </div>
      ) : board && kitchens.length === 0 ? (
        <div style={S.empty}>
          <span style={S.chip(KDS.onTime)}>
            {breachedOnly ? "No kitchen is in breach" : "Every kitchen is clear"}
          </span>
          <div style={S.emptySub}>
            {breachedOnly
              ? "Turn the breached only filter off to see everything still open."
              : "Tickets appear here the moment an order is placed."}
          </div>
        </div>
      ) : (
        <div style={S.scroller}>
        <table style={{ ...S.table, minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, ...S.sevCell }} aria-label="Severity" />
              <th style={S.th}>Kitchen</th>
              <th style={{ ...S.th, ...S.thR }}>Open</th>
              <th style={{ ...S.th, ...S.thR }}>
                <span style={S.thPip(STATUS_META.NEW.colour)} />
                Unaccepted
              </th>
              <th style={{ ...S.th, ...S.thR }}>
                <span style={S.thPip(SLA_META.AT_RISK.colour)} />
                {SLA_META.AT_RISK.label}
              </th>
              <th style={{ ...S.th, ...S.thR }}>
                <span style={S.thPip(SLA_META.LATE.colour)} />
                {SLA_META.LATE.label}
              </th>
              <th style={{ ...S.th, ...S.thR }}>
                <span style={S.thPip(SLA_META.BREACHED.colour)} />
                {SLA_META.BREACHED.label}
              </th>
              <th style={{ ...S.th, ...S.thR }}>Oldest</th>
              <th style={S.th}>State</th>
              <th style={{ ...S.th, ...S.thR }} aria-label="Expand" />
            </tr>
          </thead>
          <tbody>
            {kitchens.map((kitchen) => {
              const state = worstStateOf(kitchen);
              const meta = SLA_META[state] || SLA_META.ON_TIME;
              const isOpen = expanded.has(kitchen.key);
              const tickets = kitchen.tickets || [];

              // Worst wins, and it is always written out. Colour alone never
              // says which of "late" and "breached" a kitchen is in.
              const stateLabel =
                countOf(kitchen.breached) > 0
                  ? `${countOf(kitchen.breached)} breached`
                  : countOf(kitchen.late) > 0
                  ? `${countOf(kitchen.late)} late`
                  : countOf(kitchen.atRisk) > 0
                  ? `${countOf(kitchen.atRisk)} running close`
                  : "On time";

              return (
                <React.Fragment key={kitchen.key}>
                  <tr>
                    <td style={{ ...S.td, ...S.sevCell }}>
                      <span style={S.sev(meta.colour)} />
                    </td>
                    <td style={S.td}>
                      <span style={S.name}>
                        {kitchen.restaurantName || kitchen.vendorName || "Unnamed restaurant"}
                      </span>
                      <span style={S.nameSub}>
                        {/* The country is a code, not a flag. These screens carry
                            no emoji, and a wall panel font cannot be trusted to
                            have the flag glyphs anyway. */}
                        {kitchen.outletName || "Main store"}
                        {kitchen.country ? ` · ${String(kitchen.country).toUpperCase()}` : ""}
                      </span>
                    </td>
                    <td style={{ ...S.td, ...S.tdNum(kitchen.open) }}>{countOf(kitchen.open)}</td>
                    <td style={{ ...S.td, ...S.tdNum(kitchen.unacknowledged) }}>
                      {countOf(kitchen.unacknowledged)}
                    </td>
                    <td style={{ ...S.td, ...S.tdNum(kitchen.atRisk) }}>{countOf(kitchen.atRisk)}</td>
                    <td style={{ ...S.td, ...S.tdNum(kitchen.late) }}>{countOf(kitchen.late)}</td>
                    <td style={{ ...S.td, ...S.tdNum(kitchen.breached) }}>
                      {countOf(kitchen.breached)}
                    </td>
                    <td style={{ ...S.td, ...S.tdR }}>
                      {formatDuration(liveAge(kitchen.oldestMs), { compact: true })}
                    </td>
                    <td style={S.td}>
                      <span style={S.chip(meta.colour, state === "BREACHED")}>{stateLabel}</span>
                    </td>
                    <td style={{ ...S.td, ...S.thR }}>
                      <button
                        type="button"
                        style={S.rowButton}
                        onClick={() => toggleKitchen(kitchen.key)}
                        aria-expanded={isOpen}
                      >
                        {isOpen ? "Hide" : `${tickets.length} tickets`}
                      </button>
                    </td>
                  </tr>

                  {isOpen ? (
                    <tr>
                      <td colSpan={10} style={{ padding: 0, borderBottom: `1px solid ${KDS.border}` }}>
                        <div style={S.subWrap}>
                          {tickets.length === 0 ? (
                            <div style={{ padding: "12px 0", fontSize: 12.5, color: KDS.textDim }}>
                              No open tickets in this kitchen.
                            </div>
                          ) : (
                            tickets.map((ticket) => {
                              const ticketAge = liveAge(ticket.ageMs);
                              const ticketState = SLA_META[ticket?.sla?.state] || SLA_META.ON_TIME;
                              const stageMeta = STATUS_META[ticket.status] || {};
                              return (
                                <button
                                  key={ticket._id}
                                  type="button"
                                  style={S.subRow}
                                  onClick={() => openTicket(ticket, kitchen)}
                                >
                                  <span
                                    style={{
                                      ...S.sev(ageColour({ ...ticket, ageMs: ticketAge })),
                                      height: 18,
                                    }}
                                  />
                                  <span style={S.code}>{ticket.ticketCode || ticket.orderCode}</span>
                                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                                    {ticket.customerName || "Customer"}
                                    {ticket.deliveryArea ? ` · ${ticket.deliveryArea}` : ""}
                                  </span>
                                  <span style={{ fontSize: 11.5, color: KDS.textDim }}>
                                    {stageMeta.label || ticket.status}
                                  </span>
                                  <span
                                    style={S.chip(
                                      ticketState.colour,
                                      ticket?.sla?.state === "BREACHED"
                                    )}
                                  >
                                    {ticketState.label}
                                  </span>
                                  <span style={{ ...S.code, minWidth: 48, textAlign: "right" }}>
                                    {formatDuration(ticketAge, { compact: true })}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {board && kitchens.length ? (
        <div style={{ ...S.note, borderBottom: "none", borderTop: `1px solid ${KDS.border}` }}>
          {kitchens.length} kitchen{kitchens.length === 1 ? "" : "s"},{" "}
          {countOf(board.totalOpenTickets)} open ticket
          {countOf(board.totalOpenTickets) === 1 ? "" : "s"}.
        </div>
      ) : null}
    </div>
  );
}
